/**
 * Time formatting utilities
 */

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Compact age of a timestamp: "5m ago", "4h 47m ago", "64d 6h 12m ago".
 *
 * Minute resolution is kept at every magnitude (a two-month-old entry still
 * says which minute), while zero units are dropped so the common cases stay
 * short. Anything under a minute — or in the future, e.g. clock skew between
 * the bond and the browser — reads "just now".
 *
 * @param {number|string|Date} value - epoch ms, an ISO string, or a Date
 * @param {number} [now] - reference time in epoch ms (defaults to Date.now())
 * @returns {string} the formatted age, or '' when the value isn't a date
 */
export function formatRelativeAge(value, now = Date.now()) {
    if (value === null || value === undefined || value === '') return '';

    const then = value instanceof Date ? value.getTime() : new Date(value).getTime();
    if (Number.isNaN(then)) return '';

    const elapsed = now - then;
    if (elapsed < MINUTE_MS) return 'just now';

    const days = Math.floor(elapsed / DAY_MS);
    const hours = Math.floor((elapsed % DAY_MS) / HOUR_MS);
    const minutes = Math.floor((elapsed % HOUR_MS) / MINUTE_MS);

    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);

    return `${parts.join(' ')} ago`;
}
